// Product catalogue — frontend mirror of backend/services/productCatalog.js.
//
// Answers "which products may this tenant choose from?" for every place a
// user PICKS a product. Nothing here is tenant-specific: the platform
// ships a default list and a tenant narrows it with TenantConfig.products.
// A tenant that has configured nothing gets the platform defaults, which
// are exactly the lists that used to be hardcoded in these screens.
//
// Keep PLATFORM_PRODUCTS / PLATFORM_CONTRACT_TERMS in sync with the
// backend file — the backend is authoritative and rejects a product the
// tenant hasn't enabled, so a drift here shows up as a 400 rather than as
// silently divergent behaviour.

import { useSelector } from 'react-redux';
import { resolvePaymentTerms as resolvePaymentTermsPure } from './paymentTerms.js';

export const PLATFORM_PRODUCTS = ['PGMA', 'PGM', 'RC_PRO', 'E&O', 'IJM', 'PJ'];

export const PLATFORM_CONTRACT_TERMS = [1, 2, 3];

// i18n keys for the product codes the platform knows. A tenant-configured
// code with no entry here renders its raw code, which is the honest
// outcome — the platform can't translate a word it has never seen.
//
// `long` is the descriptive form used in the New Offer dropdown; `short`
// is the compact form used in list columns and filters, where a full
// sentence would blow out the column width.
// `document` is the carrier's own product title as it appears on generated
// contract documents. It is NOT a translation of `long` — on an AROMED
// document the product is "AROMED Medi Protect", whatever the interface
// calls it — which is exactly why it is a registered third form rather
// than something a surface improvises. Products with no carrier document
// have no `document` entry and fall back to `long`.
export const PRODUCT_LABEL_KEYS = {
  PGMA:    { long: 'offers.newOffer.lobPgma',  short: 'products.short.pgma',  document: 'products.document.pgma'  },
  PGM:     { long: 'offers.newOffer.lobPgm',   short: 'products.short.pgm',   document: 'products.document.pgm'   },
  RC_PRO:  { long: 'offers.newOffer.lobRcPro', short: 'products.short.rcPro', document: 'products.document.rcPro' },
  'E&O':   { long: 'offers.newOffer.lobEO',    short: 'products.short.eo'     },
  IJM:     { long: 'offers.newOffer.lobIjm',   short: 'products.short.ijm'    },
  PJ:      { long: 'offers.newOffer.lobPj',    short: 'products.short.pj'     },

  // ─── General-insurance lines ────────────────────────────────────────
  //
  // Deliberately NOT added to PLATFORM_PRODUCTS. That list is the platform
  // DEFAULT — what an unconfigured tenant may sell — and putting Motor and
  // Marine in it would drop eight general lines into the AROMED drawer.
  // These are labels only: a tenant that has configured one of these codes
  // in TenantConfig.products now gets a readable name instead of the raw
  // code, and a tenant that has not is completely unaffected.
  //
  // Only `long` is registered. productLabel falls back long → short, so one
  // registered name serves the picker and the list column both, which is
  // the honest answer when a line has no shorter trade name — "Marine
  // Insurance" does not abbreviate the way "RC Pro" does. No `document`
  // entry: the carrier's own product title on a generated document is
  // tenant configuration (offerDocuments.productTitles.byProduct), not
  // something the platform may name on a carrier's behalf.
  MOTOR:                  { long: 'products.long.motor'                 },
  MEDICAL:                { long: 'products.long.medical'               },
  PROPERTY:               { long: 'products.long.property'              },
  ENGINEERING:            { long: 'products.long.engineering'           },
  MARINE:                 { long: 'products.long.marine'                },
  GENERAL_LIABILITY:      { long: 'products.long.generalLiability'      },
  PROFESSIONAL_INDEMNITY: { long: 'products.long.professionalIndemnity' },
  TRAVEL:                 { long: 'products.long.travel'                },
};

// Offers created before P1-D persisted an English LABEL in `product`
// instead of the code. A stored label can never follow the interface
// language and does not match PRODUCT_LABEL_KEYS, so it used to fall
// through to the line-of-business FAMILY name and lose the product
// identity. Readers map it back; the stored rows are left alone.
//
// Mirrors LEGACY_PRODUCT_LABELS in backend/services/productCatalog.js.
export const LEGACY_PRODUCT_LABELS = {
  'Professional Liability (RC Pro)':  'RC_PRO',
  'Daily Benefit for Doctors (PGMA)': 'PGMA',
  'Daily Benefit - Employees (PGM)':  'PGM',
};

/** Normalise a stored `product` value to a product code. */
export function normaliseProductCode(stored) {
  const raw = String(stored || '').trim();
  if (!raw) return '';
  return LEGACY_PRODUCT_LABELS[raw] || raw;
}

// Policy.lineOfBusiness family enum → i18n key. Policies carry both a
// product code and a broader family; the code wins when present because
// three products (PGMA / PGM / IJM) share the daily_sickness family and
// would otherwise all display as the IJM name.
export const LOB_FAMILY_LABEL_KEYS = {
  professional_liability: 'screens.policiesScreen.productProfessionalLiability',
  daily_sickness:         'screens.policiesScreen.productDailySickness',
  legal_protection:       'screens.policiesScreen.productLegalProtection',
  surplus_lines:          'screens.policiesScreen.productSurplusLines',
};

// Product code → Policy.lineOfBusiness family. Mirrors
// OFFER_LOB_TO_POLICY_LOB in backend/controllers/policyController.js.
// Several products share a family (PGMA / PGM / IJM are all
// daily_sickness), which is why the product code has to win over the
// family whenever a record carries both.
export const PRODUCT_TO_POLICY_FAMILY = {
  PGMA:   'daily_sickness',
  PGM:    'daily_sickness',
  IJM:    'daily_sickness',
  RC_PRO: 'professional_liability',
  'E&O':  'errors_omissions',
  PJ:     'legal_protection',
};

// The families the manual New Policy form offers when the tenant has
// configured no product list. Deliberately the exact list that form
// hardcoded before this existed, so unconfigured tenants are unchanged.
export const PLATFORM_POLICY_FAMILIES = [
  'professional_liability',
  'daily_sickness',
  'legal_protection',
  'errors_omissions',
  'surplus_lines',
];

/**
 * Policy lines of business reachable from the tenant's product list.
 *
 * A tenant that only sells RC Pro / PGMA / PGM has no business being
 * offered Legal Protection or Surplus Lines on the manual policy form.
 * Unset products[] → the full platform family list.
 */
export function resolvePolicyLobFamilies(tenantConfig) {
  const configured = tenantConfig?.products;
  if (Array.isArray(configured) && configured.filter(Boolean).length) {
    const families = configured
      .map(code => PRODUCT_TO_POLICY_FAMILY[String(code || '').trim()])
      .filter(Boolean);
    const unique = [...new Set(families)];
    // A tenant whose products are all codes the platform has never seen
    // would otherwise end up with an empty dropdown — fall back rather
    // than block policy creation entirely.
    if (unique.length) return unique;
  }
  return [...PLATFORM_POLICY_FAMILIES];
}

/** The tenant's policy LOB families, live from Redux. */
export function useTenantPolicyLobFamilies() {
  const config = useSelector(state => state?.tenantConfig?.config);
  return resolvePolicyLobFamilies(config);
}

export function resolveProducts(tenantConfig) {
  const configured = tenantConfig?.products;
  if (Array.isArray(configured)) {
    const cleaned = configured.map(p => String(p || '').trim()).filter(Boolean);
    if (cleaned.length) return cleaned;
  }
  return [...PLATFORM_PRODUCTS];
}

export function resolveContractTerms(tenantConfig) {
  const configured = tenantConfig?.contractTermOptions;
  if (Array.isArray(configured)) {
    const cleaned = configured
      .map(Number)
      .filter(n => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);
    if (cleaned.length) return cleaned;
  }
  return [...PLATFORM_CONTRACT_TERMS];
}

/**
 * Translate a product code (or, failing that, a LOB family) to a display
 * label. Falls through to the raw value rather than inventing one, so an
 * unrecognised tenant product shows its code instead of a wrong name.
 *
 * @param {function} t          i18next translate
 * @param {string}   code       product code, e.g. 'RC_PRO'
 * @param {string}   [family]   Policy.lineOfBusiness family enum
 * @param {'short'|'long'|'document'} [form]
 */
export function productLabel(t, code, family, form = 'short') {
  const normalised = normaliseProductCode(code);
  const entry = PRODUCT_LABEL_KEYS[normalised];
  if (entry) return t(entry[form] || entry.long || entry.short, { defaultValue: normalised });

  // A product the built-in catalogue does not know is still THE PRODUCT.
  // Product is a free String on the record (tenant vocabulary is not an
  // enum), so a tenant naming its own products lands here — and falling
  // through to the lineOfBusiness family rendered somebody else's category
  // under a column headed "Product": MOK policies carrying
  // `product: "Travel Assistance Coverage — … (Business)"` and a stale
  // `lineOfBusiness: "legal_protection"` displayed as "Legal Protection" in
  // both list surfaces, disagreeing with their own detail screen.
  //
  // The family is a fallback for records with NO product, not an override
  // for products this catalogue happens not to recognise.
  if (code && String(code).trim()) return String(code).trim();

  const familyKey = LOB_FAMILY_LABEL_KEYS[family];
  if (familyKey) return t(familyKey, { defaultValue: family });
  return normalised || family || '—';
}

/** The tenant's product allow-list, live from Redux. */
export function useTenantProducts() {
  const config = useSelector(state => state?.tenantConfig?.config);
  return resolveProducts(config);
}

/** The tenant's contract term options (years), live from Redux. */
export function useTenantContractTerms() {
  const config = useSelector(state => state?.tenantConfig?.config);
  return resolveContractTerms(config);
}

/**
 * The tenant's PGMA field-of-activity list. Empty array means the tenant
 * has published no list, and the form keeps its free-text input.
 */
export function useTenantPgmaSpecialties() {
  const config = useSelector(state => state?.tenantConfig?.config);
  const list = config?.pgmaSpecialties;
  return Array.isArray(list) ? list.map(s => String(s || '').trim()).filter(Boolean) : [];
}

// ─── Payment terms ────────────────────────────────────────────────────
//
// The vocabulary and the resolver live in ./paymentTerms.js, which is
// deliberately free of React and Redux imports: reviseSeed.js is loaded
// directly by a Node test to assert revision seeding, so anything it pulls
// in has to be loadable without a bundler. Only the Redux-bound hook lives
// here, alongside the other useTenant* hooks.
export {
  PAYMENT_FREQUENCIES, PAYMENT_FREQUENCY_LABEL_KEYS,
  LEGACY_FREQUENCY_LABELS, normalisePaymentFrequency, resolvePaymentTerms,
  availableFrequencies,
} from './paymentTerms.js';

/** The tenant's payment terms for a product, live from Redux. */
export function useTenantPaymentTerms(productCode) {
  const config = useSelector(state => state?.tenantConfig?.config);
  return resolvePaymentTermsPure(config, productCode);
}
